import { EmailOutlined, PhoneOutlined } from '@mui/icons-material';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React from 'react';
import { useSelector } from 'react-redux';

import { LinkTextWrapper } from '../../components/global-styles/GlobalStyles';
import { colors } from '../../components/theme/theme';
import { RootState } from '../../store';
import { getSupportEmailForLocation, getSupportPhoneDisplay, getSupportPhoneTel } from '../../utils/contact-support';
import { useTranslation } from '../../utils/translations';

interface ContactSupportModalContentProps {
    introText?: string;
}

/**
 * Shared body for Contact Support modals (login, registration, migration).
 * Phone: AuxCap 877-773-0333, Avant 888-412-8268. Email by Canada vs US locale storage.
 */
export const ContactSupportModalContent: React.FC<ContactSupportModalContentProps> = ({ introText }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const brandDetails = useSelector((state: RootState) => state.brand.brandDetails);
    const portalId = brandDetails?.Id;
    const phoneDisplay = getSupportPhoneDisplay(portalId);
    const phoneTel = getSupportPhoneTel(portalId);
    const { email, mailto } = getSupportEmailForLocation();

    const primary = theme.palette.primary.main;

    return (
        <Stack spacing={2.5} sx={{ width: '100%', mt: 0.5, mb: 0.5, textAlign: 'left' }}>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65, pr: 0.5 }}>
                {introText ?? t('registration.contactSupport.introBeforePhone')}
            </Typography>

            <Box
                sx={{
                    border: `1px solid ${colors.greyPreset[30]}`,
                    borderRadius: 2,
                    px: 2.25,
                    py: 2,
                }}
            >
                <Stack spacing={0}>
                    <Stack direction="row" alignItems="center" spacing={1.75}>
                        <PhoneOutlined
                            sx={{
                                fontSize: 18,
                                color: primary,
                                flexShrink: 0,
                            }}
                            aria-hidden
                        />
                        <LinkTextWrapper
                            href={phoneTel}
                            sx={{
                                display: 'inline-block',
                                typography: 'body2',
                                fontWeight: 600,
                                lineHeight: 1.45,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                textAlign: 'left',
                            }}
                        >
                            {phoneDisplay}
                        </LinkTextWrapper>
                    </Stack>

                    <Divider sx={{ my: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }} component="span">
                            {t('registration.contactSupport.betweenPhoneAndEmail')}
                        </Typography>
                    </Divider>

                    <Stack direction="row" alignItems="flex-start" spacing={1.75}>
                        <EmailOutlined
                            sx={{
                                fontSize: 18,
                                color: primary,
                                flexShrink: 0,
                                mt: 0.125,
                            }}
                            aria-hidden
                        />
                        <LinkTextWrapper
                            href={mailto}
                            sx={{
                                display: 'inline-block',
                                typography: 'body2',
                                fontWeight: 600,
                                lineHeight: 1.45,
                                wordBreak: 'break-word',
                                overflowWrap: 'anywhere',
                                textAlign: 'left',
                            }}
                        >
                            {email}
                        </LinkTextWrapper>
                    </Stack>
                </Stack>
            </Box>
        </Stack>
    );
};
